import { isPendingConsent } from '../../atomic/helpers';
import { isEmpty } from '../../util/types';
import { useCurrentUser } from '../user';

export type IdvPosition =
    | 'profile'
    | 'address'
    | 'citizenship'
    | 'employment-status'
    | 'employer-address'
    | 'conflict-of-interest'
    | 'investment-priority'
    | 'investment-downturn-reaction'
    | 'investment-supported-values'
    | 'investment-avoid-industries'
    | 'consent'
    | 'summary'
    | 'complete';

export const IDV_POSITIONS: IdvPosition[] = [
    'profile',
    'address',
    'citizenship',
    'employment-status',
    'employer-address',
    'conflict-of-interest',
    'investment-priority',
    'investment-downturn-reaction',
    'investment-supported-values',
    'investment-avoid-industries',
    'summary',
    'consent',
];

export const useBaseIdvPosition = ({
    justSubmittedIndividualConsent,
}: {
    justSubmittedIndividualConsent: boolean;
}): IdvPosition => {
    const { userInfo } = useCurrentUser();
    const atomicStatus = userInfo.atomicStatus;

    // Legacy: anyone who has previously completed Atomic onboarding is
    // considered to be "complete".
    const isLegacyComplete =
        userInfo.fullAtomicOnboardingFinished ||
        (!!atomicStatus && !isPendingConsent(atomicStatus));

    if (isLegacyComplete || justSubmittedIndividualConsent) return 'complete';

    const profileComplete = validate([
        userInfo.firstName,
        userInfo.lastName,
        userInfo.dob,
        userInfo.phone,
    ]);

    if (!profileComplete) return 'profile';

    const addressComplete = validate([
        userInfo.address1,
        userInfo.locality,
        userInfo.region,
        userInfo.postalCode,
    ]);
    if (!addressComplete) return 'address';

    const citizenshipComplete = validate([userInfo.taxIdToken]);
    if (!citizenshipComplete) return 'citizenship';

    const employmentStatusComplete = validate(
        userInfo.onboardingData?.employmentStatus === 'EMPLOYED'
            ? [
                  userInfo.onboardingData?.employerName,
                  userInfo.onboardingData?.industry,
                  userInfo.onboardingData?.role,
                  userInfo.annualIncome,
              ]
            : userInfo.onboardingData?.employmentStatus === 'SELF_EMPLOYED'
              ? [
                    userInfo.onboardingData?.employmentStartYearMonth,
                    userInfo.onboardingData?.industry,
                    userInfo.onboardingData?.role,
                    userInfo.annualIncome,
                ]
              : [
                    userInfo.onboardingData?.employmentStatus,
                    userInfo.annualIncome,
                ]
    );

    if (!employmentStatusComplete) return 'employment-status';

    const employerAddressComplete =
        !!userInfo.onboardingData?.employmentStatus &&
        userInfo.onboardingData?.employmentStatus !== 'EMPLOYED'
            ? true
            : validate([
                  userInfo.onboardingData?.employerAddress?.address1,
                  userInfo.onboardingData?.employerAddress?.locality,
                  userInfo.onboardingData?.employerAddress?.region,
                  userInfo.onboardingData?.employerAddress?.postalCode,
              ]);

    if (!employerAddressComplete) return 'employer-address';

    if (userInfo.onboardingData?.hasConflictOfInterest !== false)
        return 'conflict-of-interest';

    if (!validate([userInfo.investmentProfile?.investment_priority]))
        return 'investment-priority';

    if (!validate([userInfo.investmentProfile?.downturn_reaction]))
        return 'investment-downturn-reaction';

    if (!validate([userInfo.investmentProfile?.supported_values]))
        return 'investment-supported-values';

    if (!validate([userInfo.investmentProfile?.avoid_industries]))
        return 'investment-avoid-industries';

    if (!userInfo.summaryPageFinished) {
        return 'summary';
    }

    if (!userInfo.atomicStatus || isPendingConsent(userInfo.atomicStatus)) {
        return 'consent';
    }

    return 'complete';
};

const validate = (fields: any[]) => fields.every((v) => !isEmpty(v));
